import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Modal } from "react-responsive-modal";
import "react-responsive-modal/styles.css";
import ToggleSwitch from "../../../components/ToggleSwitch/ToggleSwitch";
import { useAddEmployeePF } from "../../../queries/employeeQueries";
import { toast } from "react-toastify";
import { UANRegex, pfRegex } from "../../../utils/regex/regex";

// PF Number: e.g., DL/CPM/1234567/0001234 or MH/BAN/123456/000123
// UAN: 12-digit numeric
const EditOrAddEPF = ({
  openModal,
  onCloseModal,
  employee,
  pfDetails = {},
}) => {
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      pf_account_number: pfDetails?.pf_account_number || "",
      uan: pfDetails?.uan || "",
      consider_for_calculation: !!pfDetails?.consider_for_calculation,
      employee_pf_type_contribution_id:
        pfDetails?.employee_pf_type_contribution_id || 1,
    },
  });

  useEffect(() => {
    if (pfDetails) {
      reset({
        pf_account_number: pfDetails?.pf_account_number || "",
        uan: pfDetails?.uan || "",
        consider_for_calculation: !!pfDetails?.consider_for_calculation,
        employee_pf_type_contribution_id:
          pfDetails?.employee_pf_type_contribution_id || 1,
      });
    }
  }, [pfDetails, reset]);

  const { mutate: addOrEditEmployeePF } = useAddEmployeePF();

  const onSubmit = (data) => {
    const payload = {
      ...data,
      employee_id: employee?.employee_id,
    };
    addOrEditEmployeePF(payload, {
      onSuccess: (res) => {
        toast.success(res?.message || "Saved successfully");
        onCloseModal();
      },
      onError: (error) => {
        toast.error(
          error?.response?.data?.message || "Failed to save PF details"
        );
      },
    });
  };

  const employee_pf_type_contribution_id = 2;
  const pf_contribution_id = 1;

  return (
    <Modal open={openModal} onClose={onCloseModal} center>
      <div className="p-5 w-[380px]">
        <h2 className="text-lg text-[#1F2937] font-semibold mb-5">
          Edit/Add EPF
        </h2>
        <form
          key={JSON.stringify(pfDetails)}
          className="flex flex-col gap-4"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="w-[332px]">
            <label className="text-sm font-medium text-black">
              PF Account Number <span className="text-red-700">*</span>
            </label>
            <input
              type="text"
              className="h-[38px] w-full border rounded px-2"
              {...register("pf_account_number", {
                required: "PF Account Number is required",
                pattern: {
                  value: pfRegex,
                  message:
                    "Format: e.g., DL/CPM/1234567/0001234 or MH/BAN/123456/000123",
                },
              })}
            />
            {errors.pf_account_number && (
              <p className="text-sm text-red-600 mt-1">
                {errors.pf_account_number.message}
              </p>
            )}
          </div>

          <div>
            <p className="text-sm text-black font-medium mb-3">
              PF Excess Contribution
            </p>
            <div className="flex mb-3">
              <input
                type="radio"
                id="radio1"
                value={pf_contribution_id}
                {...register("employee_pf_type_contribution_id")}
                defaultChecked={
                  pfDetails?.employee_pf_type_contribution_id ===
                    pf_contribution_id ||
                  (!pfDetails.employee_pf_type_contribution_id &&
                    Number(pf_contribution_id) === Number(pf_contribution_id))
                }
              />
              <label htmlFor="radio1" className="text-sm text-black ml-2">
                12% of Actual PF Wage
              </label>
            </div>
            <div className="flex">
              <input
                type="radio"
                id="radio2"
                value={employee_pf_type_contribution_id}
                {...register("employee_pf_type_contribution_id")}
                defaultChecked={
                  pfDetails?.employee_pf_type_contribution_id ===
                  employee_pf_type_contribution_id
                }
              />
              <label htmlFor="radio2" className="text-sm text-black ml-2">
                Restrict Contribution to 15,000 of PF Wage
              </label>
            </div>
          </div>

          <div className="w-[332px]">
            <label className="text-sm font-medium text-black">
              UAN <span className="text-red-700">*</span>
            </label>
            <input
              type="text"
              className="h-[38px] w-full border rounded px-2"
              {...register("uan", {
                required: "UAN is required",
                pattern: {
                  value: UANRegex,
                  message: "UAN must be a 12-digit number",
                },
              })}
            />
            {errors.uan && (
              <p className="text-sm text-red-600 mt-1">{errors.uan.message}</p>
            )}
          </div>

          <div className="flex items-center mb-10">
            <input
              type="checkbox"
              className="mr-2"
              {...register("consider_for_calculation")}
              onChange={(e) =>
                setValue("consider_for_calculation", e.target.checked)
              }
            />
            <p className="text-sm font-normal text-black">
              Contribute to Employee Pension Scheme
            </p>
          </div>

          <div className="flex justify-end gap-3 mr-2">
            <button
              type="button"
              className="btn btn--border text-sm font-medium"
              onClick={onCloseModal}
            >
              Cancel
            </button>
            <button type="submit" className="btn text-sm font-medium">
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default EditOrAddEPF;
